/**
 * Asset Method Step — stage 2 of the asset wizard.
 *
 * Three cards:
 *   1. **Add one** — manual single-asset path. Routes to `asset-details`.
 *   2. **Upload spreadsheet** — bulk-XLSX path. Routes to
 *      `asset-bulk-upload` (the dropzone + template download).
 *   3. **Use existing** — deep-links to Manage Assets so the user can
 *      attach activities to an asset that already exists.
 *
 * Driven by `formData.assetType` chosen on the preceding `asset-select`
 * step. Wizard chrome owns Back — chrome Back from here returns to
 * `asset-select` and clears the type.
 */

import { Pencil, FileSpreadsheet, Building2 } from 'lucide-react';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { ActivitySelectionCard } from '../ActivitySelectionCard';
import { AssetType } from '../../types/emissions';
import {
  isBulkImportableAssetType,
} from '@g17eco/emissions-config';
import { ASSET_TYPE_BRANDING } from '../../config/assetTypeBranding';

interface AssetMethodStepProps {
  /** The asset type chosen on the preceding `asset-select` step. */
  assetType: AssetType;
  /** Manual path: routes parent to `asset-details`. */
  onChooseManual: () => void;
  /** Bulk path: routes parent to `asset-bulk-upload`. */
  onChooseBulk: () => void;
  /** Existing path: deep-link to Manage Assets. */
  onChooseExisting: () => void;
}

export const AssetMethodStep = ({
  assetType,
  onChooseManual,
  onChooseBulk,
  onChooseExisting,
}: AssetMethodStepProps) => {
  const brand = ASSET_TYPE_BRANDING[assetType] ?? ASSET_TYPE_BRANDING[AssetType.Other];
  const canBulk = isBulkImportableAssetType(assetType);

  return (
    <Card className='tw:mb-6'>
      <CardContent className='tw:p-6'>
        <div className='tw:flex tw:items-center tw:gap-3 tw:mb-4'>
          <div className={`tw:rounded-full tw:flex tw:items-center tw:justify-center tw:size-10 ${brand.iconBg}`}>
            <brand.Icon className={`tw:size-5 ${brand.iconColor}`} />
          </div>
          <div>
            <h5 className='tw:font-semibold tw:mb-0'>{brand.label}</h5>
            <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>{brand.description}</p>
          </div>
        </div>

        <h6 className='tw:font-semibold tw:mb-3'>How would you like to add?</h6>
        <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4'>
          <ActivitySelectionCard
            Icon={Pencil}
            iconBg='tw:bg-emerald-100'
            iconColor='tw:text-emerald-700'
            label='Create a new asset'
            description={`Fill in the details for a single ${brand.label.toLowerCase()} and add activities one at a time.`}
            onClick={onChooseManual}
          />
          <ActivitySelectionCard
            Icon={FileSpreadsheet}
            iconBg='tw:bg-emerald-100'
            iconColor='tw:text-emerald-700'
            label='Upload XLSX'
            description={canBulk
              ? 'Best for many assets at once. Download the XLSX template, fill it in, and upload here.'
              : `Use a spreadsheet upload isn't available for ${brand.label}.`}
            onClick={canBulk ? onChooseBulk : undefined}
            isDisabled={!canBulk}
          />
          <ActivitySelectionCard
            Icon={Building2}
            iconBg='tw:bg-emerald-100'
            iconColor='tw:text-emerald-700'
            label='Add to existing asset'
            description='Add activities to an asset you already registered. Opens Manage Assets.'
            onClick={onChooseExisting}
          />
        </div>
      </CardContent>
    </Card>
  );
};
